import type {
    PaymentMethodChange,
    PaymentMethodChangeTarget,
} from '@/lib/api/sales-order-api'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { formatDateTimeVN, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** Khoản thu trên đơn hoặc phiếu thu cần đổi hình thức. */
export interface MethodChangeSubject {
    amount: number
    payment_method: string
    /** Mã lỗi vì sao không đổi được; vắng = đổi được. */
    method_change_block?: string
}

interface Props {
    /** Khoản / phiếu đang chọn; null = đóng. */
    subject: MethodChangeSubject | null
    /** Câu nói thêm phạm vi đổi (cả phiếu thu, phiếu dư đổi theo…). */
    note?: string
    pending: boolean
    onOpenChange: (open: boolean) => void
    onConfirm: (target: PaymentMethodChangeTarget) => void
}

/** Hai hình thức đổi qua lại được (M-152). */
export function isSwitchablePayment(
    method: string,
): method is PaymentMethodChangeTarget {
    return method === 'cash' || method === 'bank_transfer'
}

export function paymentMethodLabel(method: string): string {
    if (method === 'bank_transfer') return m.payment_method_bank()
    if (method === 'card') return m.payment_method_card()
    return m.payment_method_cash()
}

export function otherMethod(method: string): PaymentMethodChangeTarget {
    return method === 'cash' ? 'bank_transfer' : 'cash'
}

/** Các lần đã đổi hình thức: cũ → mới, ai, lúc nào. */
export function MethodChangeLines({
    changes,
}: {
    changes?: PaymentMethodChange[]
}) {
    return (
        <>
            {changes?.map((c) => (
                <div key={c.id} className="text-xs text-muted-foreground/70">
                    {m.order_payment_method_change_history({
                        from: paymentMethodLabel(c.from_method),
                        to: paymentMethodLabel(c.to_method),
                        name: c.changed_by_name,
                        time: formatDateTimeVN(c.created_at),
                    })}
                </div>
            ))}
        </>
    )
}

/**
 * Xác nhận đổi Tiền mặt ↔ Chuyển khoản (M-152). Khoản bị chặn (đối soát
 * chuyển khoản, đơn KiotViet…) mở ra để thấy lý do, chỉ có nút Đóng.
 */
export function ChangePaymentMethodDialog({
    subject,
    note,
    pending,
    onOpenChange,
    onConfirm,
}: Props) {
    const blocked = subject?.method_change_block
        ? (resolveApiErrorMessage(subject.method_change_block) ??
          m.order_payment_method_change_error())
        : null
    const from = subject ? paymentMethodLabel(subject.payment_method) : ''
    const target = otherMethod(subject?.payment_method ?? 'cash')
    const to = paymentMethodLabel(target)

    return (
        <Dialog open={subject !== null} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {blocked
                            ? m.order_payment_method_change_blocked_title()
                            : m.order_payment_method_change_title()}
                    </DialogTitle>
                    <DialogDescription>
                        {blocked ??
                            m.order_payment_method_change_description({
                                amount: formatVnd(subject?.amount ?? 0),
                                from,
                                to,
                            })}
                    </DialogDescription>
                </DialogHeader>
                {!blocked && note && (
                    <p className="text-sm text-muted-foreground">{note}</p>
                )}
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={pending}
                        onClick={() => onOpenChange(false)}
                    >
                        {blocked ? m.common_close() : m.common_cancel()}
                    </Button>
                    {!blocked && subject && (
                        <Button
                            type="button"
                            disabled={pending}
                            onClick={() => onConfirm(target)}
                        >
                            {m.order_payment_method_change_confirm({ to })}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
